import { Permission } from '../store/interfaces/user'

type PermissionOrString = Permission | string;

const normalizePermission = (permission: PermissionOrString): string => {
    if (typeof permission === 'string') return permission
    if (permission !== null && typeof permission === 'object' && 'name' in permission) {
        return permission.name
    }
    return ''
}

/**
 * Convierte array de permisos a strings normalizados
 */
const normalizePermissions = (permissions: PermissionOrString[] | undefined): string[] => {
    if (!permissions) return []
    return permissions.map(normalizePermission).filter(Boolean)
}

/**
 * Verifica si el usuario tiene un permiso específico
 * @param permissions - Permisos del usuario
 * @param requiredPermissions - Permisos requeridos (array)
 * @returns true si el usuario tiene al menos uno de los permisos requeridos
 */
export const hasPermission = (
    permissions: PermissionOrString[] | undefined,
    requiredPermissions: string[]
): boolean => {
    const normalizedUserPerms = normalizePermissions(permissions)
    const normalizedRequired = requiredPermissions.map(normalizePermission)

    if (
        normalizedRequired.length === 0
    ) {
        return true
    }

    return normalizedRequired.some((permission) =>
        normalizedUserPerms.includes(permission)
    )
}

/**
 * Verifica si el usuario tiene TODOS los permisos especificados
 * @param permissions - Permisos del usuario
 * @param requiredPermissions - Permisos requeridos (array)
 * @returns true si el usuario tiene todos los permisos requeridos
 */
export const hasAllPermissions = (
    permissions: PermissionOrString[] | undefined,
    requiredPermissions: string[]
): boolean => {
    const normalizedUserPerms = normalizePermissions(permissions)
    const normalizedRequired = requiredPermissions.map(normalizePermission)

    if (
        normalizedRequired.length === 0
    ) {
        return true
    }

    return normalizedRequired.every((permission) =>
        normalizedUserPerms.includes(permission)
    )
}

/**
 * Verifica si el usuario tiene NINGUNO de los permisos especificados
 * @param permissions - Permisos del usuario
 * @param forbiddenPermissions - Permisos prohibidos (array)
 * @returns true si el usuario no tiene ninguno de los permisos prohibidos
 */
export const hasNoPermissions = (
    permissions: PermissionOrString[] | undefined,
    forbiddenPermissions: string[]
): boolean => {
    const normalizedUserPerms = normalizePermissions(permissions)
    const normalizedForbidden = forbiddenPermissions.map(normalizePermission)

    if (
        normalizedForbidden.length === 0
    ) {
        return true
    }

    return !normalizedForbidden.some((permission) =>
        normalizedUserPerms.includes(permission)
    )
}

/**
 * Obtiene los permisos del usuario desde la información de permisos
 * @param userPermissions - Objeto con información de permisos del usuario
 * @returns Array normalizado de permisos
 */
export const extractPermissions = (userPermissions: { permissions?: PermissionOrString[] } | PermissionOrString[] | undefined): string[] => {
    if (!userPermissions) return []

    let perms: PermissionOrString[] | undefined;
    if (Array.isArray(userPermissions)) {
        perms = userPermissions;
    } else {
        perms = userPermissions.permissions;
    }

    return normalizePermissions(perms);
}

/**
 * Filtra un array de items basado en permisos
 * @param items - Array de items a filtrar
 * @param permissions - Permisos del usuario
 * @param getRequiredPermissions - Función que extrae los permisos requeridos de cada item
 * @returns Array filtrado de items
 */
export const filterByPermissions = <T>(
    items: T[],
    permissions: PermissionOrString[] | undefined,
    getRequiredPermissions: (item: T) => string[] | undefined
): T[] => {
    return items.filter((item) => {
        const requiredPermissions = getRequiredPermissions(item)
        if (!requiredPermissions || requiredPermissions.length === 0) {
            return true
        }
        return hasPermission(permissions, requiredPermissions)
    })
}
